import React, { useEffect, useRef, useState } from "react"
import { Box, Image, Portal } from "@chakra-ui/react"
import {
  AnimatePresence, MotionConfig, motion, useReducedMotion,
} from "framer-motion"
import { UpdateImage } from "./types"

// d.io/updates' zoom (dio-marketing zoomable-image.tsx): the picture grows from its place to
// the middle of the screen and shrinks back. Esc, a click or a scroll closes it.
// Reduced motion: a fade.

const SPRING = { type: "spring", bounce: 0, duration: 0.4 } as const

type Rect = { left: number, top: number, width: number, height: number }
type Place = { scale: number, x: number, y: number }

const AT_REST: Place = { scale: 1, x: 0, y: 0 }

const placeOf = (image: HTMLImageElement | null, rect: Rect): Place => {
  const from = image?.getBoundingClientRect()

  return from
    ? { scale: from.width / rect.width, x: from.left - rect.left, y: from.top - rect.top }
    : AT_REST
}

type Props = {
  image: UpdateImage
  sizes?: string
}

const ZoomableImage = ({ image, sizes }: Props) => {
  const thumb = useRef<HTMLImageElement>(null)
  // Mounted until the zoom out has finished
  const [open, setOpen] = useState(false)
  const [zoomed, setZoomed] = useState(false)
  const [rect, setRect] = useState<Rect | null>(null)
  const [atThumb, setAtThumb] = useState<Place>(AT_REST)
  const reduce = useReducedMotion()

  const show = () => {
    const img = thumb.current
    if (!img?.naturalWidth || !img.naturalHeight) {
      return
    }
    const margin = window.innerWidth < 640 ? 16 : 48
    const fullWidth = image.width ?? img.naturalWidth
    const ratio = fullWidth / (image.height ?? img.naturalHeight)
    const width = Math.min(
      window.innerWidth - margin * 2,
      (window.innerHeight - margin * 2) * ratio,
      fullWidth,
    )
    const height = width / ratio
    const target = {
      left: (window.innerWidth - width) / 2, top: (window.innerHeight - height) / 2, width, height,
    }
    setRect(target)
    setAtThumb(placeOf(img, target))
    setOpen(true)
    setZoomed(true)
  }

  const hide = () => {
    if (rect) {
      setAtThumb(placeOf(thumb.current, rect))
    }
    setZoomed(false)
  }

  useEffect(() => {
    if (!zoomed) {
      return undefined
    }
    const close = () => hide()
    // Capture phase, so Escape closes the zoom and not the modal around it
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== "Escape") {
        return
      }
      event.stopPropagation()
      hide()
    }
    window.addEventListener("wheel", close, { passive: true })
    window.addEventListener("touchmove", close, { passive: true })
    window.addEventListener("keydown", onKeyDown, true)

    return () => {
      window.removeEventListener("wheel", close)
      window.removeEventListener("touchmove", close)
      window.removeEventListener("keydown", onKeyDown, true)
    }
  })

  const variants = {
    away: (place: Place) => (reduce ? { opacity: 0 } : { ...place, opacity: 1 }),
    shown: { opacity: 1, ...AT_REST },
  }

  return (
    <MotionConfig reducedMotion="user">
      <Box
        as="button"
        type="button"
        display="block"
        w="full"
        cursor="zoom-in"
        aria-label={image.alt ? undefined : "Enlarge image"}
        onClick={show}
        _focusVisible={{ outline: "2px solid", outlineColor: "blue.500", outlineOffset: "4px" }}
      >
        <Image
          ref={thumb}
          src={image.src}
          srcSet={image.srcSet}
          sizes={image.srcSet ? sizes : undefined}
          alt={image.alt}
          htmlWidth={image.width}
          htmlHeight={image.height}
          loading="lazy"
          decoding="async"
          display="block"
          w="full"
          h="auto"
          visibility={open && !reduce ? "hidden" : undefined}
        />
      </Box>
      {open && (
        <Portal>
          <Box
            role="dialog"
            aria-label={image.alt || "Image"}
            position="fixed"
            inset={0}
            zIndex="tooltip"
            cursor="zoom-out"
            onClick={hide}
          >
            <AnimatePresence custom={atThumb} onExitComplete={() => setOpen(false)}>
              {zoomed && rect && (
                <motion.div
                  key="overlay"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={SPRING}
                  style={{ position: "fixed", inset: 0, background: "rgba(0, 0, 0, 0.8)" }}
                />
              )}
              {zoomed && rect && (
                <motion.img
                  key="image"
                  src={image.src}
                  alt=""
                  custom={atThumb}
                  variants={variants}
                  initial="away"
                  animate="shown"
                  exit="away"
                  transition={SPRING}
                  style={{
                    position: "fixed", ...rect, originX: 0, originY: 0,
                  }}
                />
              )}
            </AnimatePresence>
          </Box>
        </Portal>
      )}
    </MotionConfig>
  )
}

export default ZoomableImage
